// Case-study content for /work. Browser-safe: plain data plus asset URLs.
//
// Every image here is a real screenshot of the live product, taken from the
// public site. Copy sticks to what the product visibly does; no invented
// metrics. Adding a project is a data edit here, the layouts adapt.

import outfitlinkDesktop from "@/assets/work/outfitlink/desktop.webp";
import outfitlinkMobile from "@/assets/work/outfitlink/mobile.webp";
import outfitlinkExplore from "@/assets/work/outfitlink/detail-1.webp";

import mooodyDesktop from "@/assets/work/mooody/desktop.webp";
import mooodyMobile from "@/assets/work/mooody/mobile.webp";
import mooodyHow from "@/assets/work/mooody/detail-1.webp";

import moooDesktop from "@/assets/work/moooments/desktop.webp";
import moooMobile from "@/assets/work/moooments/mobile.webp";
import moooBook from "@/assets/work/moooments/detail-1.webp";
import moooShoots from "@/assets/work/moooments/detail-2.webp";

import jciDesktop from "@/assets/work/just-convert-it/desktop.webp";
import jciMobile from "@/assets/work/just-convert-it/mobile.webp";
import jciImage from "@/assets/work/just-convert-it/detail-1.webp";
import jciSticker from "@/assets/work/just-convert-it/detail-2.webp";

import unstatusDesktop from "@/assets/work/unstatus/desktop.webp";
import unstatusMobile from "@/assets/work/unstatus/mobile.webp";
import unstatusGuide from "@/assets/work/unstatus/detail-1.webp";

import easyLoverDesktop from "@/assets/work/easy-lover/desktop.webp";
import easyLoverWide from "@/assets/easy-lover/easy-lover-wide-artwork.png.asset.json";

import yimcDesktop from "@/assets/work/youth-in-mission/desktop.webp";
import yimcMobile from "@/assets/work/youth-in-mission/mobile.webp";
import yimcMedia from "@/assets/work/youth-in-mission/detail-1.webp";
import yimcPhotos from "@/assets/work/youth-in-mission/detail-2.webp";

import waldDesktop from "@/assets/work/waldfrieden/desktop.webp";
import waldMobile from "@/assets/work/waldfrieden/mobile.webp";
import waldEvents from "@/assets/work/waldfrieden/detail-1.webp";

export type Shot = { src: string; alt: string; w: number; h: number };

export type WorkItem = {
  slug: string;
  /** Product name as it appears in its own UI. */
  name: string;
  /** Live product URL. */
  url: string;
  /** Hostname shown in the browser frame. */
  host: string;
  /** One line about the work, used as the headline. */
  title: string;
  /** Two sentences at most. */
  summary: string;
  category: string;
  year: string;
  /** Brand accent, used as a soft tint behind the device frames. */
  accent: string;
  /** Product ships a dark interface; the browser chrome follows. */
  dark?: boolean;
  cover: Shot;
  mobile?: Shot;
  gallery: Shot[];
  story: { heading: string; body: string }[];
  built: string[];
  stack: string[];
  facts: { value: string; label: string }[];
};

const D = { w: 1600, h: 1000 };
const M = { w: 600, h: 1298 };

export const WORK: WorkItem[] = [
  {
    slug: "outfitlink",
    name: "Outfitlink",
    url: "https://outfitlink.co/",
    host: "outfitlink.co",
    title: "Create and share outfits with one link",
    summary:
      "A fashion platform for creators and shoppers. Build an outfit from any product link, publish it as a page, and let people shop the look.",
    category: "Consumer platform",
    year: "2026",
    accent: "#E14F62",
    cover: { src: outfitlinkDesktop, alt: "Outfitlink home page with the outfit builder and Mix & Match sets", ...D },
    mobile: { src: outfitlinkMobile, alt: "Outfitlink on a phone: paste a product link or upload an outfit picture", ...M },
    gallery: [
      { src: outfitlinkExplore, alt: "Outfitlink explore page with outfits filtered by style and occasion", ...D },
    ],
    story: [
      {
        heading: "The idea",
        body: "Outfit inspiration lives in screenshots and saved posts. Outfitlink turns it into something you can build, share and buy from.",
      },
      {
        heading: "What we built",
        body: "A drag and drop outfit builder, a virtual closet, product imports from retailers, creator profiles and style hubs. Plus an AI shoot director that photographs the outfit for you.",
      },
      {
        heading: "Under the hood",
        body: "TanStack Start on Supabase and Drizzle, image generation for the shoots, and a public API with agent tools so assistants can build outfits too.",
      },
    ],
    built: ["Outfit builder", "Virtual closet", "Retailer imports", "AI shoot director", "Creator profiles", "Agent API"],
    stack: ["TanStack Start", "Supabase", "Drizzle", "Image generation", "WebMCP"],
    facts: [
      { value: "1 link", label: "Per outfit, shoppable" },
      { value: "Any store", label: "Paste a product URL" },
    ],
  },
  {
    slug: "mooody",
    name: "mooody",
    url: "https://mooody-io.lovable.app/",
    host: "mooody-io.lovable.app",
    title: "Moodboards with citations",
    summary:
      "A moodboard tool for design students and researchers. Paste a link, get the image, and keep the source attached in the citation style your school wants.",
    category: "Design tool",
    year: "2026",
    accent: "#593BED",
    dark: true,
    cover: { src: mooodyDesktop, alt: "mooody landing page: Moodboard Creator for design students, with a 3D render of the canvas", ...D },
    mobile: { src: mooodyMobile, alt: "mooody on a phone: moodboards with citations", ...M },
    gallery: [{ src: mooodyHow, alt: "mooody How it works page: from link to layout in seconds", ...D }],
    story: [
      {
        heading: "The idea",
        body: "Moodboards look great and cite nothing. In design school that costs marks, in a studio it costs trust.",
      },
      {
        heading: "What we built",
        body: "Paste and discover, automatic source research, canvas formats you can configure and high resolution exports with figure numbers. Cloud backup, credit bundles and a blog.",
      },
      {
        heading: "Under the hood",
        body: "React and Supabase, with n8n workflows doing the source research and Stripe handling credits.",
      },
    ],
    built: ["Paste to canvas", "Source research", "Six citation styles", "Diorama layout", "Print-ready exports", "Credits and billing"],
    stack: ["React", "Supabase", "n8n", "Stripe"],
    facts: [
      { value: "6", label: "Citation styles" },
      { value: "1 paste", label: "From link to layout" },
    ],
  },
  {
    slug: "moooments",
    name: "moooments",
    url: "https://moooments.com/",
    host: "moooments.com",
    title: "Golden hour bookings for a photography studio",
    summary:
      "The whole business of a portrait photographer in one product: sessions, golden hour availability, deposits, client galleries and invoices.",
    category: "Booking platform",
    year: "2026",
    accent: "#2F55E6",
    cover: { src: moooDesktop, alt: "moooments home page with recent stories, an availability calendar and session prices", ...D },
    mobile: { src: moooMobile, alt: "moooments on a phone with the story carousel", ...M },
    gallery: [
      { src: moooBook, alt: "moooments booking flow, step one: choose a session", ...D },
      { src: moooShoots, alt: "moooments shoots page: people, where they live", ...D },
    ],
    story: [
      {
        heading: "The idea",
        body: "Every session happens in the last hour of light. The booking flow had to know when that is, wherever the client is.",
      },
      {
        heading: "What we built",
        body: "A four step booking flow with sunrise and sunset data per location, live availability from Google Calendar, Stripe deposits and balances, client portals, email and invoicing.",
      },
      {
        heading: "Under the hood",
        body: "TanStack Start on Supabase and Drizzle, Stripe for payments, React Email for every message the client receives.",
      },
    ],
    built: ["Golden hour booking", "Calendar sync", "Deposits and balances", "Client portals", "Lead tracking", "Invoicing"],
    stack: ["TanStack Start", "Supabase", "Stripe", "Google Calendar", "React Email"],
    facts: [
      { value: "4 steps", label: "From session to booked" },
      { value: "Sun data", label: "Per location and date" },
    ],
  },
  {
    slug: "just-convert-it",
    name: "Just Convert It",
    url: "https://just-convert-it.com/",
    host: "just-convert-it.com",
    title: "27 file tools that never upload your files",
    summary:
      "Convert, compress, trim and extract in the browser. Nothing leaves your machine, it works offline, and there is no account.",
    category: "Web utility",
    year: "2026",
    accent: "#1783FF",
    cover: { src: jciDesktop, alt: "Just Convert It home page: drop a file, or pick a tool", ...D },
    mobile: { src: jciMobile, alt: "Just Convert It on a phone with the file drop zone", ...M },
    gallery: [
      { src: jciImage, alt: "Just Convert It image converter", ...D },
      { src: jciSticker, alt: "Just Convert It sticker and emoji maker for Slack and Discord", ...D },
    ],
    story: [
      {
        heading: "The idea",
        body: "Most online converters are upload forms with ads. We wanted the opposite: fast, private and free.",
      },
      {
        heading: "What we built",
        body: "27 tools for images, video, audio, PDF and design files. Batch workflows, a sticker maker with exact Slack and Discord specs, and an installable app that runs offline.",
      },
      {
        heading: "Under the hood",
        body: "WebCodecs for video, pdf.js for documents, ag-psd for design files. Playwright tests cover every tool.",
      },
    ],
    built: ["Image, video and audio tools", "PDF merge and split", "Design file extractors", "Batch workflows", "Offline PWA", "Sticker maker"],
    stack: ["React", "WebCodecs", "pdf.js", "PWA", "Playwright"],
    facts: [
      { value: "27", label: "Tools" },
      { value: "0", label: "Files uploaded" },
    ],
  },
  {
    slug: "unstatus",
    name: "Unstatus",
    url: "https://unstatus.lovable.app/",
    host: "unstatus.lovable.app",
    title: "One page that tells you why Lovable feels slow",
    summary:
      "An unofficial status dashboard for the services Lovable depends on. Incidents, timelines and browser notifications in one dark page.",
    category: "Status dashboard",
    year: "2026",
    accent: "#007AFF",
    dark: true,
    cover: { src: unstatusDesktop, alt: "Unstatus dashboard with active incidents, a 30 day activity chart and service cards", ...D },
    mobile: { src: unstatusMobile, alt: "Unstatus on a phone showing the incident chart", ...M },
    gallery: [{ src: unstatusGuide, alt: "Unstatus guide: which AI models does Lovable use", ...D }],
    story: [
      {
        heading: "The idea",
        body: "When a build fails, the cause is often upstream. Nobody wants to open eight status pages to find out.",
      },
      {
        heading: "What we built",
        body: "A dashboard that polls upstream status sources on the server, groups them by category, draws 30 days of incident activity and pushes browser notifications for the services you pick.",
      },
      {
        heading: "Under the hood",
        body: "TanStack Start and React 19, Supabase functions for cached polling, a public JSON feed and an llms.txt for agents.",
      },
    ],
    built: ["Incident timeline", "Category preferences", "Browser notifications", "Public JSON feed", "AI model guide"],
    stack: ["TanStack Start", "React 19", "Supabase"],
    facts: [
      { value: "30 s", label: "Refresh" },
      { value: "30 days", label: "Incident history" },
    ],
  },
  {
    slug: "easy-lover",
    name: "Easy Lover",
    url: "https://vibing.inc/easy-lover",
    host: "chromewebstore.google.com",
    title: "A Chrome extension that makes Lovable's toolbar yours",
    summary:
      "Pin and reorder the pages you use in Lovable's editor, keep GitHub settings next to Share, and save the layout on your machine.",
    category: "Browser extension",
    year: "2026",
    accent: "#2B3BFF",
    dark: true,
    cover: { src: easyLoverDesktop, alt: "Easy Lover open inside the Lovable editor, with pinned toolbar pages", w: 1600, h: 829 },
    gallery: [{ src: easyLoverWide.url, alt: "Easy Lover artwork", w: 1983, h: 793 }],
    story: [
      {
        heading: "The idea",
        body: "Lovable's toolbar shows the same panels to everyone. People who live in the editor wanted their own order and fewer clicks.",
      },
      {
        heading: "What we built",
        body: "A small extension that adds pinning and reordering to the toolbar and remembers your layout locally. No account needed. Free on the Chrome Web Store.",
      },
    ],
    built: ["Pin toolbar pages", "Reorder by drag", "GitHub beside Share", "Layout saved locally"],
    stack: ["Chrome extension", "TypeScript"],
    facts: [
      { value: "Free", label: "On the Chrome Web Store" },
      { value: "Local", label: "Your layout stays with you" },
    ],
  },
  {
    slug: "youth-in-mission",
    name: "Youth in Mission",
    url: "https://youthinmission.info/",
    host: "youthinmission.info",
    title: "A bilingual home for a five day congress",
    summary:
      "Program, speakers, media library, photo galleries, livestream and registration for a youth congress in the DACH region. German and English, edited by the team.",
    category: "Event platform",
    year: "2026",
    accent: "#00B8D9",
    dark: true,
    cover: { src: yimcDesktop, alt: "Youth in Mission Congress home page with the 2027 dates over a photo of the audience", ...D },
    mobile: { src: yimcMobile, alt: "Youth in Mission Congress on a phone", ...M },
    gallery: [
      { src: yimcMedia, alt: "Youth in Mission media library filtered by year", ...D },
      { src: yimcPhotos, alt: "Youth in Mission photo albums by year", ...D },
    ],
    story: [
      {
        heading: "The idea",
        body: "A congress lives for five days a year and online for the other 360. The site has to sell the next one and archive the last one.",
      },
      {
        heading: "What we built",
        body: "Program schedule, speaker pages, a media library by year, masonry photo galleries, phase based registration with tickets, a livestream page and a custom admin so the team edits everything themselves.",
      },
      {
        heading: "Under the hood",
        body: "React 19, TanStack Start and Supabase, with the CMS built in rather than bolted on.",
      },
    ],
    built: ["Program and speakers", "Media library", "Photo galleries", "Registration and tickets", "Livestream", "Custom admin"],
    stack: ["TanStack Start", "React 19", "Supabase"],
    facts: [
      { value: "DE / EN", label: "Fully bilingual" },
      { value: "Built in", label: "Admin and CMS" },
    ],
  },
  {
    slug: "waldfrieden",
    name: "Waldfrieden",
    url: "https://sta-waldfrieden.de/",
    host: "sta-waldfrieden.de",
    title: "A church website its community actually runs",
    summary:
      "Events, services, groups and donations for a congregation in Munich, kept current through the tools the team already uses.",
    category: "Community platform",
    year: "2026",
    accent: "#7A1F2E",
    cover: { src: waldDesktop, alt: "Waldfrieden home page: Willkommen zuhause over a photo of the congregation", ...D },
    mobile: { src: waldMobile, alt: "Waldfrieden on a phone with address and service times", ...M },
    gallery: [{ src: waldEvents, alt: "Waldfrieden events page with category filters and the next event", ...D }],
    story: [
      {
        heading: "The idea",
        body: "Community sites go stale because updating them is a chore. This one syncs from ChurchTools and takes instructions over Telegram.",
      },
      {
        heading: "What we built",
        body: "Event and service listings synced from ChurchTools, room booking, donations with a scannable GiroCode, campaign pages for sermon series, verse of the day and admin roles.",
      },
      {
        heading: "Under the hood",
        body: "React 19, TanStack and Supabase, with edge functions for the ChurchTools sync and the Telegram assistant.",
      },
    ],
    built: ["ChurchTools sync", "Room booking", "GiroCode donations", "Sermon campaigns", "Telegram admin", "Verse of the day"],
    stack: ["React 19", "TanStack", "Supabase", "ChurchTools", "Telegram"],
    facts: [
      { value: "ChurchTools", label: "Events and rooms, synced" },
      { value: "Telegram", label: "Admin from the phone" },
    ],
  },
];

export function findWork(slug: string): WorkItem | undefined {
  return WORK.find((w) => w.slug === slug);
}

/** The four projects the homepage shows. */
export const FEATURED_WORK = WORK.slice(0, 4);
